import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, QuoteIcon, MoonIcon, SparklesIcon } from 'lucide-react';
type Testimonial = {
  name: string;
  label: string;
  quote: string;
  highlight: string;
  initials: string;
  avatarBg: string;
  avatarText: string;
};
const TESTIMONIALS: Testimonial[] = [{
  name: 'Carla M.',
  label: 'Mamá de niño con TDAH',
  quote: 'Pensé que iba a ser otra guía más que terminaba guardada en el celular. Pero no. La primera noche ya sentí una diferencia. No fue magia, pero hubo menos resistencia, menos gritos y por fin una rutina que pude sostener sin agotarme.',
  highlight: 'Más calma nocturna',
  initials: 'CM',
  avatarBg: 'bg-rose-100',
  avatarText: 'text-rose-700'
}, {
  name: 'Florencia R.',
  label: 'Mamá de niño con TEA',
  quote: 'Lo que más me gustó fue que no sentí que me hablaban de un niño "ideal", sino del mío. Las actividades sensoriales realmente nos ayudaron a bajar revoluciones. Mi hijo empezó a anticipar mejor la rutina y las crisis nocturnas bajaron muchísimo.',
  highlight: 'Menos crisis al dormir',
  initials: 'FR',
  avatarBg: 'bg-sky-100',
  avatarText: 'text-sky-700'
}, {
  name: 'Valeria G.',
  label: 'Mamá de 2 hijos',
  quote: 'Yo llegaba a la noche destruida. Ya no sabía qué hacer, y terminábamos todos mal. Con el Método D.P.C. sentí alivio porque por fin tenía pasos claros. Hoy las noches no son perfectas, pero ya no son una guerra.',
  highlight: 'Más alivio para mamá',
  initials: 'VG',
  avatarBg: 'bg-emerald-100',
  avatarText: 'text-emerald-700'
}, {
  name: 'Romina P.',
  label: 'Mamá de niño con TDAH',
  quote: 'Lo compré por desesperación, sinceramente. Y me sorprendió. Las transiciones dejaron de ser tan pesadas y eso cambió toda la noche. Antes apagar una pantalla era un drama, ahora tengo herramientas concretas para que no explote todo.',
  highlight: 'Menos resistencia',
  initials: 'RP',
  avatarBg: 'bg-amber-100',
  avatarText: 'text-amber-700'
}, {
  name: 'Marcela T.',
  label: 'Mamá de niño con TEA y TDAH',
  quote: 'Además de ayudarme con mi hijo, me ayudó a mí. Entender cómo regular antes de dormir me sacó mucha culpa. Ya no siento que estoy improvisando todo el tiempo. Me devolvió esa sensación de "ok, ahora sé qué hacer".',
  highlight: 'Rutina más fácil',
  initials: 'MT',
  avatarBg: 'bg-violet-100',
  avatarText: 'text-violet-700'
}];
function TestimonialCard({
  t,
  index
}: {
  t: Testimonial;
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6,
    delay: index % 3 * 0.1
  }} className="bg-white rounded-3xl p-7 md:p-8 shadow-[0_8px_30px_rgba(47,72,99,0.08)] hover:shadow-[0_12px_40px_rgba(47,72,99,0.12)] transition-shadow duration-300 flex flex-col relative overflow-hidden h-full" data-id="element-1">
      {/* Decorative quote mark in background */}
      <QuoteIcon className="absolute -top-2 -right-2 w-24 h-24 text-[#A8BFA3]/10" fill="currentColor" strokeWidth={0} data-id="element-2" />

      {/* Stars */}
      <div className="flex gap-1 mb-5 relative z-10" data-id="element-3">
        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 text-[#D8B36A]" fill="#D8B36A" strokeWidth={0} data-id="element-4" />)}
      </div>

      {/* Quote */}
      <p className="text-[#333333] text-[15px] leading-relaxed flex-1 mb-6 relative z-10" data-id="element-5">
        {t.quote}
      </p>

      {/* Author */}
      <div className="flex items-center gap-3 pt-5 border-t border-[#F0EBE3] relative z-10" data-id="element-6">
        <div className={`w-12 h-12 rounded-full ${t.avatarBg} ${t.avatarText} flex items-center justify-center flex-shrink-0`} data-id="element-7">
          <span className="font-bold text-sm" data-id="element-8">{t.initials}</span>
        </div>
        <div className="flex-1 min-w-0" data-id="element-9">
          <p className="font-bold text-[#2F4863] text-sm leading-tight" data-id="element-10">
            {t.name}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 leading-tight" data-id="element-11">
            {t.label}
          </p>
        </div>
      </div>

      {/* Highlight badge */}
      <div className="mt-4 inline-flex self-start items-center gap-1.5 bg-[#A8BFA3]/15 text-[#2F4863] px-3 py-1.5 rounded-full text-xs font-bold" data-id="element-12">
        <SparklesIcon className="w-3 h-3" data-id="element-13" />
        <span data-id="element-14">{t.highlight}</span>
      </div>
    </motion.article>;
}
export function WhatsAppTestimonials() {
  return <section className="bg-[#FAF7F2] px-6 py-20 md:py-28 relative overflow-hidden" data-id="element-15">
      {/* Decorative bg shapes */}
      <div className="absolute top-20 -left-20 w-72 h-72 bg-[#A8BFA3]/10 rounded-full blur-3xl pointer-events-none" data-id="element-16"></div>
      <div className="absolute bottom-20 -right-20 w-96 h-96 bg-[#D8B36A]/10 rounded-full blur-3xl pointer-events-none" data-id="element-17"></div>
      <MoonIcon className="absolute top-10 right-10 md:top-16 md:right-20 w-16 h-16 md:w-20 md:h-20 text-[#2F4863]/5 -rotate-12" strokeWidth={1.5} data-id="element-18" />
      <SparklesIcon className="absolute bottom-10 left-10 md:bottom-20 md:left-16 w-12 h-12 md:w-16 md:h-16 text-[#D8B36A]/20" strokeWidth={1.5} data-id="element-19" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-20">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14 md:mb-16" data-id="element-21">
          <p className="text-xs md:text-sm font-bold tracking-[0.2em] text-[#A8BFA3] uppercase mb-4" data-id="element-22">
            Testimonios reales
          </p>
          <h2 className="font-['Fraunces'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#2F4863] mb-5 leading-tight max-w-3xl mx-auto" data-id="element-23">
            Lo que otras mamás ya están viviendo en casa
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-24">
            No son promesas. Son noches más tranquilas, menos peleas y madres
            que por fin sienten alivio.
          </p>
        </motion.div>

        {/* Top row: 3 cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-7 mb-6 md:mb-7" data-id="element-25">
          {TESTIMONIALS.slice(0, 3).map((t, i) => <TestimonialCard key={i} t={t} index={i} data-id="element-26" />)}
        </div>

        {/* Bottom row: 2 cards centered */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-7 md:max-w-4xl md:mx-auto" data-id="element-27">
          {TESTIMONIALS.slice(3, 5).map((t, i) => <TestimonialCard key={i + 3} t={t} index={i + 3} data-id="element-28" />)}
        </div>
      </div>
    </section>;
}